<template>
  <div>
    <div class="d-flex align-center flex-wrap">
      <v-btn v-if="folderIds.length > 0" text small color="primary" class="px-1" @click="toggleAll">
        <v-icon small left class="no-gradient">{{ allOpen ? 'mdi-collapse-all-outline' : 'mdi-expand-all-outline' }}</v-icon>
        {{ allOpen ? $t('offers.attachments.close_all') : $t('offers.attachments.open_all') }}
      </v-btn>
      <v-spacer />
      <v-btn text small color="primary" class="px-1" :loading="zipping === ''" :disabled="zipping !== null" @click="downloadZip(null)">
        <v-icon small left class="no-gradient">mdi-folder-download-outline</v-icon>
        {{ $t('offers.attachments.download_all') }}
      </v-btn>
    </div>
    <v-treeview :items="tree" :open.sync="open" item-key="id" item-text="name" dense hoverable open-on-click>
      <template v-slot:prepend="{ item, open: folderOpen }">
        <v-icon class="no-gradient">{{ item.children ? (folderOpen ? 'mdi-folder-open-outline' : 'mdi-folder-outline') : fileIcon(item.file) }}</v-icon>
      </template>
      <template v-slot:label="{ item }">
        <template v-if="item.children">
          <span class="body-2">{{ item.name }}</span>
          <span class="caption grey--text ml-2">{{ $tc('offers.attachments.file_count', item.count, { n: item.count }) }}</span>
        </template>
        <a v-else href="#" class="body-2" @click.prevent.stop="$emit('open', item.file)">{{ item.name }}</a>
      </template>
      <template v-slot:append="{ item }">
        <template v-if="item.children">
          <v-btn icon small :title="$t('offers.attachments.download_folder')" :loading="zipping === item.path" :disabled="zipping !== null" @click.stop="downloadZip(item)">
            <v-icon small class="no-gradient">mdi-folder-download-outline</v-icon>
          </v-btn>
          <v-btn v-if="!readonly" icon small :title="$t('offers.attachments.delete_folder')" @click.stop="$emit('remove-folder', item)">
            <v-icon small class="no-gradient" color="error">mdi-delete</v-icon>
          </v-btn>
        </template>
        <template v-else>
          <v-btn icon small :title="$t('offers.attachments.download')" @click.stop="$emit('download', item.file)">
            <v-icon small class="no-gradient">mdi-download</v-icon>
          </v-btn>
          <v-btn v-if="!readonly" icon small :title="$t('delete')" @click.stop="$emit('remove', item.file)">
            <v-icon small class="no-gradient" color="error">mdi-delete</v-icon>
          </v-btn>
        </template>
      </template>
    </v-treeview>
  </div>
</template>

<script>
import downloadjs from 'downloadjs';

// Kansiot ensin ja numerot luvun mukaan (L2 ennen L10:tä)
function sortTree(node) {
  node.children.sort((a, b) => !!b.children - !!a.children || a.name.localeCompare(b.name, 'fi', { numeric: true }));
  node.count = node.children.reduce((n, child) => n + (child.children ? sortTree(child) : 1), 0);
  return node.count;
}

// Liitteet kansiopuuna (folder = kansiopolku). Kansion tai kaikki liitteet saa zippinä.
export default {
  name: 'OfferFileList',
  props: {
    files: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    // folder => Promise<Blob>; folder = kansion polku, null = kaikki
    zip: { type: Function, required: true },
    zipName: { type: String, required: true },
  },
  data() {
    return {
      open: [],
      // Zipattavan kansion polku, '' = kaikki
      zipping: null,
    };
  },
  computed: {
    tree() {
      const root = { children: [] };
      const folders = { '': root };
      const folder = path => {
        if (!folders[path]) {
          const slash = path.lastIndexOf('/');
          const node = { id: `dir:${path}`, path, name: path.slice(slash + 1), children: [] };
          folder(slash < 0 ? '' : path.slice(0, slash)).children.push(node);
          folders[path] = node;
        }
        return folders[path];
      };
      this.files.forEach(f => folder(f.folder || '').children.push({ id: f.key, name: f.filename, file: f }));
      sortTree(root);
      return root.children;
    },
    folderIds() {
      const ids = new Set();
      this.files.forEach(f => {
        const parts = (f.folder || '').split('/').filter(Boolean);
        parts.forEach((part, i) => ids.add(`dir:${parts.slice(0, i + 1).join('/')}`));
      });
      return [...ids];
    },
    allOpen() {
      return this.folderIds.length > 0 && this.folderIds.every(id => this.open.includes(id));
    },
  },
  methods: {
    toggleAll() {
      this.open = this.allOpen ? [] : [...this.folderIds];
    },
    fileIcon(file) {
      const type = file.contentType || '';
      if (type.includes('pdf')) return 'mdi-file-pdf-box';
      if (type.startsWith('image')) return 'mdi-file-image';
      if (type.includes('word') || type.includes('rtf')) return 'mdi-file-word';
      if (type.includes('sheet') || type.includes('excel')) return 'mdi-file-excel';
      return 'mdi-file-document-outline';
    },
    async downloadZip(folder) {
      this.zipping = folder ? folder.path : '';
      try {
        downloadjs(await this.zip(folder ? folder.path : null), `${folder ? folder.name : this.zipName}.zip`, 'application/zip');
      } catch (error) {
        this.$handleApiError(error, this.$t('offers.attachments.load_failed'));
      }
      this.zipping = null;
    },
  },
};
</script>
